import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BOARD_LIMITS, BOARD_WARNINGS, type LimitKind, type SceneStore } from '@os/doc';
import { Button } from '../../ui';

const KINDS: readonly LimitKind[] = ['objects', 'images'];

/**
 * The board quotas as the editor says them (spec « Quotas applicatifs », P12a): an alert when a local addition is
 * refused (nothing was added) or, once per kind and board, when the board is full (also on opening), and a note, once
 * per kind and board, past the warning threshold.
 */
export function BoardLimitNotice({ store }: { store: SceneStore }) {
  const { t } = useTranslation();
  const [reached, setReached] = useState<LimitKind | null>(null);
  const [near, setNear] = useState<LimitKind | null>(null);
  const warned = useRef(new Set<LimitKind>());

  useEffect(() => store.subscribeLimit(setReached), [store]);
  useEffect(() => {
    let size = -1;
    const check = () => {
      const objects = store.doc.getMap('objects').size;
      if (objects === size) return; // counted only when objects come or go, not on every move
      size = objects;
      const counts = store.counts();
      for (const k of KINDS) {
        if (counts[k] < BOARD_WARNINGS[k] || warned.current.has(k)) continue;
        warned.current.add(k);
        if (counts[k] >= BOARD_LIMITS[k]) setReached(k);
        else setNear(k);
      }
    };
    check();
    return store.subscribeList(check);
  }, [store]);

  if (!reached && !near) return null;
  const box = 'pointer-events-auto max-w-md rounded-lg border bg-surface p-3 font-sans text-sm text-fg shadow-lg';
  return (
    <div className="pointer-events-none absolute left-1/2 top-4 z-20 flex -translate-x-1/2 flex-col gap-2">
      {reached && (
        <div role="alert" className={`${box} border-accent`}>
          <p>{t(`limits.reached.${reached}`)}</p>
          <Button variant="ghost" size="sm" onClick={() => setReached(null)} className="mt-2">
            {t('limits.close')}
          </Button>
        </div>
      )}
      {near && !reached && (
        <div role="status" className={`${box} border-border`}>
          <p>{t(`limits.near.${near}`)}</p>
          <Button variant="ghost" size="sm" onClick={() => setNear(null)} className="mt-2">
            {t('limits.close')}
          </Button>
        </div>
      )}
    </div>
  );
}
